import { useEffect, useState } from "react";
import { FileText, Check, X, Clock, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_LABEL = {
  sent: { label: "En attente", color: "bg-brand/10 text-brand", icon: Clock },
  accepted: { label: "Accepté", color: "bg-forest/10 text-forest", icon: Check },
  rejected: { label: "Refusé", color: "bg-slate-200 text-slate2", icon: X },
};

/**
 * Carte inline dans le fil de messagerie qui affiche un devis reçu/envoyé.
 * Côté particulier : boutons Accepter / Refuser.
 */
export default function QuoteCard({ quoteId, currentRole, onUpdated }) {
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/quotes/${quoteId}`)
      .then((r) => { if (alive) setQ(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [quoteId]);

  const respond = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/quotes/${quoteId}/status`, { status });
      setQ((prev) => ({ ...prev, status }));
      toast.success(status === "accepted" ? "Devis accepté" : "Devis refusé");
      onUpdated?.(status);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!q) {
    return (
      <div className="bg-white border border-border rounded-2xl p-3 max-w-[86%] animate-pulse">
        <div className="h-3 w-24 bg-slate-100 rounded mb-2" />
        <div className="h-4 w-40 bg-slate-100 rounded" />
      </div>
    );
  }

  const s = STATUS_LABEL[q.status] || STATUS_LABEL.sent;
  const SIcon = s.icon;

  return (
    <div className="bg-white border border-border rounded-2xl p-3.5 max-w-[86%] w-full shadow-sm" data-testid={`quote-card-${q.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="h-8 w-8 rounded-xl bg-pro/10 text-pro flex items-center justify-center">
          <FileText className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-sm text-ink" data-testid={`quote-number-${q.id}`}>Devis {q.number}</div>
          <div className="text-[11px] text-slate2 truncate">{q.pro_company || q.pro_name} · {q.items.length} ligne{q.items.length > 1 ? "s" : ""}</div>
        </div>
        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full flex items-center gap-1 ${s.color}`}>
          <SIcon className="w-3 h-3" /> {s.label}
        </span>
      </div>

      <ul className="space-y-1 mb-2.5">
        {q.items.slice(0, 4).map((it, idx) => (
          <li key={idx} className="flex items-center justify-between text-xs">
            <span className="text-slate2 truncate mr-2">
              {it.description} <span className="text-slate-400">× {it.quantity} {it.unit}</span>
            </span>
            <span className="text-ink font-semibold shrink-0">
              {(it.quantity * it.unit_price_ht).toFixed(2)} €
            </span>
          </li>
        ))}
        {q.items.length > 4 && (
          <li className="text-[11px] text-slate2 italic">+ {q.items.length - 4} autre(s) ligne(s)…</li>
        )}
      </ul>

      {q.notes && (
        <div className="text-[11px] text-slate2 italic border-l-2 border-border pl-2 mb-2">{q.notes}</div>
      )}

      <div className="bg-bone rounded-xl p-2 flex items-center justify-between text-xs mb-2">
        <span className="text-slate2">Total TTC ({q.tva_rate}% TVA)</span>
        <span className="font-heading font-bold text-ink text-base" data-testid={`quote-ttc-${q.id}`}>{q.total_ttc.toFixed(2)} €</span>
      </div>

      <div className="text-[10px] text-slate2 uppercase tracking-wider mb-1">
        Valable jusqu&apos;au {new Date(q.valid_until).toLocaleDateString("fr-FR")}
      </div>

      <button
        onClick={async () => {
          try {
            const r = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        }}
        className="w-full h-8 mt-1 rounded-full border border-border text-xs text-slate2 font-semibold flex items-center justify-center gap-1.5 tap-scale hover:border-brand hover:text-brand"
        data-testid={`quote-pdf-btn-${q.id}`}
      >
        <Download className="w-3.5 h-3.5" /> Télécharger le PDF
      </button>

      {currentRole === "particulier" && q.status === "sent" && (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <button
            onClick={() => respond("rejected")}
            disabled={busy}
            className="h-9 rounded-full border border-border text-slate2 text-xs font-semibold tap-scale disabled:opacity-50"
            data-testid={`quote-reject-btn-${q.id}`}
          >
            Refuser
          </button>
          <button
            onClick={() => respond("accepted")}
            disabled={busy}
            className="h-9 rounded-full bg-forest text-white text-xs font-semibold tap-scale disabled:opacity-50"
            data-testid={`quote-accept-btn-${q.id}`}
          >
            Accepter
          </button>
        </div>
      )}
    </div>
  );
}
